import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import SectionShell from './SectionShell'

const t = buildTranslate('recovery_ai.promo.instant_recovery')

const SECTION_PADDING = '48px 86px'
const SECTION_GAP = '48px'
const COMPARISON_CARD_WIDTH = '554px'
const COMPARISON_CARD_RADIUS = '8px'
const COMPARISON_COLUMN_PADDING = '36px 24px'
// Figma "Elevation/3" — drop shadow 0/8/24 at Grey-90 20%. No theme token exists
// for elevation, so the value is spelled out here.
const COMPARISON_CARD_SHADOW = '0px 8px 24px rgba(54, 66, 78, 0.2)'
const STAT_LINE_HEIGHT = '56px'

type StatBlockProps = {
  value: string
  label: string
  /** Grey-90 for the "without" column, Nephritis for the "with" column. */
  valueColor: string
}

const StatBlock = ({ value, label, valueColor }: StatBlockProps) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        textAlign: 'center',
        width: '100%',
      })}
    >
      <Text.Header3
        tag="p"
        color={valueColor}
        className={css({ lineHeight: '28px', marginBottom: 0 })}
      >
        {value}
      </Text.Header3>
      <Text.BodyText3
        tag="p"
        color="grey70"
        className={css({ lineHeight: '24px', marginBottom: 0 })}
      >
        {label}
      </Text.BodyText3>
    </div>
  )
}

type ComparisonColumnProps = {
  title: string
  responseTime: string
  responseRate: string
  valueColor: string
}

const ComparisonColumn = ({
  title,
  responseTime,
  responseRate,
  valueColor,
}: ComparisonColumnProps) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        alignItems: 'center',
        backgroundColor: palette.white,
        display: 'flex',
        flex: '1 0 0',
        flexDirection: 'column',
        gap: '24px',
        minWidth: 0,
        padding: COMPARISON_COLUMN_PADDING,
      })}
    >
      <Text.BodyText4
        tag="p"
        bold
        className={css({
          lineHeight: '20px',
          marginBottom: 0,
          textAlign: 'center',
          width: '100%',
        })}
      >
        {title}
      </Text.BodyText4>
      <StatBlock
        value={responseTime}
        label={t('response_time_label')}
        valueColor={valueColor}
      />
      <div
        className={css({
          backgroundColor: palette.grey30,
          height: '1px',
          width: '100%',
        })}
      />
      <StatBlock
        value={responseRate}
        label={t('response_rate_label')}
        valueColor={valueColor}
      />
    </div>
  )
}

const InstantRecovery = () => {
  const [css] = useStyletron()

  return (
    <SectionShell padding={SECTION_PADDING}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: SECTION_GAP,
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <Text.Header2
            tag="h2"
            className={css({ lineHeight: '40px', marginBottom: 0 })}
          >
            {t('title')}
          </Text.Header2>
          <Text.BodyText3
            tag="p"
            color="grey70"
            className={css({ lineHeight: '24px', marginBottom: 0 })}
          >
            {t('subtitle')}
          </Text.BodyText3>
        </div>

        <div
          className={css({
            alignItems: 'center',
            display: 'flex',
            gap: SECTION_GAP,
          })}
        >
          <div
            className={css({
              display: 'flex',
              flex: '1 0 0',
              flexDirection: 'column',
              gap: '16px',
              minWidth: 0,
            })}
          >
            <Text.SmallCaps2
              tag="p"
              bold
              className={css({ lineHeight: '12px', marginBottom: 0 })}
            >
              {t('eyebrow')}
            </Text.SmallCaps2>
            <div
              className={css({
                alignItems: 'center',
                display: 'flex',
                gap: '12px',
              })}
            >
              <Text.Header1
                tag="p"
                color={palette.spearmint50}
                className={css({
                  lineHeight: STAT_LINE_HEIGHT,
                  marginBottom: 0,
                  whiteSpace: 'nowrap',
                })}
              >
                {t('stat')}
              </Text.Header1>
              <Text.BodyText2
                tag="p"
                className={css({
                  lineHeight: '24px',
                  marginBottom: 0,
                  whiteSpace: 'nowrap',
                })}
              >
                {t('stat_caption_prefix')}{' '}
                <Text.BodyText2 bold>{t('stat_caption_brands')}</Text.BodyText2>
              </Text.BodyText2>
            </div>
            <Text.BodyText3
              tag="p"
              color="grey70"
              className={css({ lineHeight: '24px', marginBottom: 0 })}
            >
              {t('body')}
            </Text.BodyText3>
          </div>

          <div
            className={css({
              alignItems: 'stretch',
              backgroundColor: palette.white,
              borderRadius: COMPARISON_CARD_RADIUS,
              boxShadow: COMPARISON_CARD_SHADOW,
              display: 'flex',
              flexShrink: 0,
              overflow: 'hidden',
              width: COMPARISON_CARD_WIDTH,
            })}
          >
            <ComparisonColumn
              title={t('without_title')}
              responseTime={t('without_response_time')}
              responseRate={t('without_response_rate')}
              valueColor={palette.grey90}
            />
            <ComparisonColumn
              title={t('with_title')}
              responseTime={t('with_response_time')}
              responseRate={t('with_response_rate')}
              valueColor={palette.nephritis}
            />
          </div>
        </div>
      </div>
    </SectionShell>
  )
}

export default InstantRecovery
